import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Package,
  Plus,
  Search,
  Filter,
  Calendar,
  User,
  Phone,
  Building,
  Activity,
  Droplet,
  X,
  ChevronDown,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { STORAGE_KEYS } from "../../services/api";
import { fetchBloodRequests } from "../../services/api";

// User Type Detection
const getUserTypeFlags = async () => {
  try {
    const bpartnerData = localStorage.getItem(STORAGE_KEYS.B_PARTNER);
    if (bpartnerData) {
      const data = JSON.parse(bpartnerData);
      return {
        isBDO: !!data.isbdo,
        isHospital: !!data.isHospital,
        isCollection: !!(data.IsEmployee || data.IsVolunteer),
        isPublicUser: !!(
          data.isdonor &&
          !data.isbdo &&
          !data.isHospital &&
          !(data.IsEmployee || data.IsVolunteer)
        ),
      };
    }
  } catch (err) {
    console.error("Error parsing bpartner data:", err);
  }
  return {
    isBDO: false,
    isHospital: false,
    isCollection: false,
    isPublicUser: false,
  };
};

const BloodRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [userType, setUserType] = useState({ isBDO: false, isHospital: false });

  // Filter popup state
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [filterDateFrom, setFilterDateFrom] = useState(null);
  const [filterDateTo, setFilterDateTo] = useState(null);
  const [filterBloodGroup, setFilterBloodGroup] = useState("all");
  const [filterPatientName, setFilterPatientName] = useState("all");

  const filterPopupRef = useRef(null);

  useEffect(() => {
    loadBloodRequests();
    loadUserType();
  }, []);

  // Handle clicking outside filter popup to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterPopupRef.current &&
        !filterPopupRef.current.contains(event.target)
      ) {
        setShowFilterPopup(false);
      }
    };

    if (showFilterPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilterPopup]);

  const loadUserType = async () => {
    try {
      const userTypeFlags = await getUserTypeFlags();
      setUserType(userTypeFlags);
    } catch (err) {
      console.error("Failed to load user type:", err);
    }
  };

  const loadBloodRequests = async () => {
    try {
      setLoading(true);
      // Fetch real blood requests from the backend
      const realRequests = await fetchBloodRequests();
      setRequests(realRequests);
      // Store in localStorage for details page access
      localStorage.setItem("bloodRequests", JSON.stringify(realRequests));
    } catch (error) {
      console.error("Failed to load blood requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "drafted":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "voided":
        return "bg-red-100 text-red-800";
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Helper to safely get identifier or fallback to string
  const safeIdentifier = (val) => {
    if (!val) return "N/A";
    if (typeof val === "string" || typeof val === "number") return val;
    if (typeof val === "object" && val.identifier) return val.identifier;
    return JSON.stringify(val); // fallback for debugging
  };

  // Get unique values for filter dropdowns
  const getUniqueBloodGroups = () => {
    const bloodGroups = requests
      .map(
        (request) =>
          `${safeIdentifier(request.bloodGroup)} ${safeIdentifier(
            request.rhFactor
          )}`
      )
      .filter((bloodGroup) => bloodGroup && bloodGroup !== "N/A N/A");
    return [...new Set(bloodGroups)].sort();
  };

  const getUniquePatientNames = () => {
    const patientNames = requests
      .map((request) => safeIdentifier(request.patientName))
      .filter((patientName) => patientName && patientName !== "N/A");
    return [...new Set(patientNames)].sort();
  };

  // Filter actions
  const clearAllFilters = () => {
    setFilterStatus("all");
    setFilterDateFrom(null);
    setFilterDateTo(null);
    setFilterBloodGroup("all");
    setFilterPatientName("all");
  };

  const applyFilters = () => {
    setShowFilterPopup(false);
  };

  const filteredRequests = requests.filter((request) => {
    const docStatus = safeIdentifier(request.docStatus).toLowerCase();
    const bloodGroupWithRh = `${safeIdentifier(
      request.bloodGroup
    )} ${safeIdentifier(request.rhFactor)}`.toLowerCase();
    const patientName = safeIdentifier(request.patientName).toLowerCase();

    const matchesSearch =
      patientName.includes(searchTerm.toLowerCase()) ||
      safeIdentifier(request.documentNo)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      safeIdentifier(request.Help)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      safeIdentifier(request.bloodGroup)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      safeIdentifier(request.rhFactor)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      bloodGroupWithRh.includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === "all" || docStatus === filterStatus.toLowerCase();

    const matchesDateRange = (() => {
      if (!filterDateFrom && !filterDateTo) return true;
      const requestDate = new Date(request.dateOrdered);
      if (isNaN(requestDate.getTime())) return true; // Invalid date, include it

      if (filterDateFrom && filterDateTo) {
        const fromDate = new Date(filterDateFrom);
        const toDate = new Date(filterDateTo);
        // Set time to start/end of day for proper comparison
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(23, 59, 59, 999);
        return requestDate >= fromDate && requestDate <= toDate;
      } else if (filterDateFrom) {
        const fromDate = new Date(filterDateFrom);
        fromDate.setHours(0, 0, 0, 0);
        return requestDate >= fromDate;
      } else if (filterDateTo) {
        const toDate = new Date(filterDateTo);
        toDate.setHours(23, 59, 59, 999);
        return requestDate <= toDate;
      }
      return true;
    })();

    const matchesBloodGroup =
      filterBloodGroup === "all" ||
      bloodGroupWithRh.includes(filterBloodGroup.toLowerCase());

    const matchesPatientName =
      filterPatientName === "all" ||
      patientName.includes(filterPatientName.toLowerCase());

    return (
      matchesSearch &&
      matchesStatus &&
      matchesDateRange &&
      matchesBloodGroup &&
      matchesPatientName
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading blood requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center mb-2">
            {userType.isHospital ? (
              <Droplet className="h-6 w-6 text-red-600 mr-2" />
            ) : userType.isBDO ? (
              <Activity className="h-6 w-6 text-green-600 mr-2" />
            ) : null}
            <h1 className="text-2xl font-bold text-gray-900">Blood Requests</h1>
          </div>
          <p className="text-gray-600">
            Manage blood requests and track their status
          </p>
        </div>
        <button
          onClick={() => navigate("/blood-requests/new")}
          className="btn-primary flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Request
        </button>
      </div>

      {/* Filters and Search */}
      <div className="card p-2 sm:p-4 md:p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by document no, patient name, blood group, or comments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm sm:text-base"
              />
            </div>
          </div>
          <div className="flex gap-2 relative">
            <button
              onClick={() => setShowFilterPopup(!showFilterPopup)}
              className="btn-secondary flex items-center text-sm sm:text-base relative"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filter
              {(filterStatus !== "all" ||
                filterDateFrom ||
                filterDateTo ||
                filterBloodGroup !== "all" ||
                filterPatientName !== "all") && (
                <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full"></span>
              )}
            </button>

            {/* Filter Popup */}
            {showFilterPopup && (
              <div
                ref={filterPopupRef}
                className="filter-popup absolute top-full right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Advanced Filters
                  </h3>
                  <button
                    onClick={() => setShowFilterPopup(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    >
                      <option value="all">All Status</option>
                      <option value="drafted">Drafted</option>
                      <option value="completed">Completed</option>
                      <option value="voided">Voided</option>
                    </select>
                  </div>

                  {/* Date Range Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date Range
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          From
                        </label>
                        <div className="relative">
                          <DatePicker
                            selected={filterDateFrom}
                            onChange={(date) => setFilterDateFrom(date)}
                            dateFormat="MMM d, yyyy"
                            placeholderText="Select date"
                            showPopperArrow={false}
                            popperClassName="datepicker-popper"
                            popperPlacement="bottom-start"
                            maxDate={filterDateTo || undefined}
                            wrapperClassName="w-full"
                          />
                          <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          To
                        </label>
                        <div className="relative">
                          <DatePicker
                            selected={filterDateTo}
                            onChange={(date) => setFilterDateTo(date)}
                            dateFormat="MMM d, yyyy"
                            placeholderText="Select date"
                            showPopperArrow={false}
                            popperClassName="datepicker-popper"
                            popperPlacement="bottom-start"
                            minDate={filterDateFrom || undefined}
                            wrapperClassName="w-full"
                          />
                          <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Blood Group Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Blood Group
                    </label>
                    <select
                      value={filterBloodGroup}
                      onChange={(e) => setFilterBloodGroup(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    >
                      <option value="all">All Blood Groups</option>
                      {getUniqueBloodGroups().map((bloodGroup) => (
                        <option key={bloodGroup} value={bloodGroup}>
                          {bloodGroup}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Patient Name Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient Name
                    </label>
                    <select
                      value={filterPatientName}
                      onChange={(e) => setFilterPatientName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    >
                      <option value="all">All Patients</option>
                      {getUniquePatientNames().map((patientName) => (
                        <option key={patientName} value={patientName}>
                          {patientName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-4 border-t border-gray-200">
                    <button
                      onClick={clearAllFilters}
                      className="flex-1 px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      Clear All
                    </button>
                    <button
                      onClick={applyFilters}
                      className="flex-1 px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Requests List */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Document No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Blood Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Comments
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRequests.map((request) => (
                <tr key={request.id} className="hover:bg-gray-50">
                  {/* DocumentNo Column */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {safeIdentifier(request.documentNo)}
                  </td>

                  {/* Date Column */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                      {request.dateOrdered
                        ? new Date(request.dateOrdered).toLocaleDateString()
                        : "N/A"}
                    </div>
                  </td>

                  {/* Patient Name Column */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-primary-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {safeIdentifier(request.patientName)}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Phone className="h-3 w-3 mr-1" />
                          {safeIdentifier(request.mobileNo)}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Blood Group Column */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      {safeIdentifier(request.bloodGroup)}{" "}
                      {safeIdentifier(request.rhFactor)}
                    </span>
                  </td>

                  {/* Status Column */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        safeIdentifier(request.docStatus)
                      )}`}
                    >
                      {safeIdentifier(request.docStatus)}
                    </span>
                  </td>

                  {/* Comments Column */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-red-600">
                      {safeIdentifier(request.Help)}
                    </div>
                  </td>

                  {/* Actions Column */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() =>
                          navigate(`/blood-requests/${request.id}`)
                        }
                        className="text-primary-600 hover:text-primary-900"
                      >
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredRequests.length === 0 && (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">No blood requests found</p>
            <p className="text-sm text-gray-400 mt-2">
              {searchTerm || filterStatus !== "all"
                ? "Try adjusting your search or filter criteria"
                : "No blood requests have been submitted yet"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BloodRequests;
